import { MoonfallCloud } from '../convex-client.js';
import { CardBackRaster } from './card-back-raster.js';
let CardbackArt = {};

(function () {
    'use strict';


    const STOCK_URL = '/assets/images/cardback.webp';
    const RETRY_DELAYS_MS = [1000, 3000, 8000, 20000, 30000];
    const CATALOG_TIMEOUT_MS = 12000;
    const DETACHED_GRACE_MS = 1000;
    const states = new WeakMap();
    const activeOwners = new Set();
    let catalog = null;
    let catalogRequest = null;
    let catalogEpoch = 0;
    let detachObserver = null;
    let lifecycleWakesInstalled = false;

    function normalizedSlug(value) {
        const slug = typeof value === 'string' ? value.trim() : '';
        return slug && slug !== 'default' ? slug : 'default';
    }

    function decodedPathname(value) {
        let pathname;
        try {
            pathname = new URL(value, window.location?.href || 'http://localhost/').pathname;
        } catch (_) {
            return '';
        }
        for (let count = 0; count < 4; count += 1) {
            try {
                const decoded = decodeURIComponent(pathname);
                if (decoded === pathname) break;
                pathname = decoded;
            } catch (_) {
                return '';
            }
        }
        return pathname.replace(/\/{2,}/g, '/').toLowerCase();
    }

    function sanitizeUrl(value) {
        if (typeof value !== 'string') return '';
        const source = value.trim();
        if (!source || /[\u0000-\u001f\u007f]/.test(source)) return '';
        if (decodedPathname(source) === '/assets/art_placeholder.webp') return '';
        try {
            const parsed = new URL(source, window.location?.href || 'http://localhost/');
            if (parsed.protocol === 'data:') {
                return /^data:image\//i.test(source) ? source : '';
            }
            if (!['http:', 'https:', 'blob:'].includes(parsed.protocol)) return '';
            if (parsed.username || parsed.password) return '';
            return source;
        } catch (_) {
            return '';
        }
    }

    function isCurrent(owner, state) {
        return !state.retired && states.get(owner) === state;
    }

    function hasArmedRetry() {
        for (const owner of activeOwners) {
            const state = states.get(owner);
            if (state && state.retryTimer !== null && owner.isConnected) return true;
        }
        return false;
    }

    function syncLifecycleWakes() {
        const shouldInstall = hasArmedRetry();
        if (shouldInstall === lifecycleWakesInstalled) return;
        lifecycleWakesInstalled = shouldInstall;
        const method = shouldInstall ? 'addEventListener' : 'removeEventListener';
        window[method]('online', wakeRetries);
        window[method]('pageshow', wakeRetries);
        document[method]('visibilitychange', wakeVisibleRetries);
    }

    function stopDetachObserverWhenIdle() {
        if (activeOwners.size || !detachObserver) return;
        detachObserver.disconnect();
        detachObserver = null;
    }

    function retire(owner) {
        const state = states.get(owner);
        if (state) {
            state.retired = true;
            if (state.retryTimer !== null) clearTimeout(state.retryTimer);
            state.retryTimer = null;
            state.wakeRequested = false;
            states.delete(owner);
        }
        activeOwners.delete(owner);
        stopDetachObserverWhenIdle();
        syncLifecycleWakes();
    }

    function sweepDetachedOwners() {
        activeOwners.forEach((owner) => {
            const state = states.get(owner);
            if (!state) return;
            if (owner.isConnected) state.everConnected = true;
            else if (state.everConnected || Date.now() - state.createdAt >= DETACHED_GRACE_MS) {
                retire(owner);
            }
        });
    }

    function track(owner) {
        activeOwners.add(owner);
        if (detachObserver || typeof MutationObserver !== 'function') return;
        detachObserver = new MutationObserver(sweepDetachedOwners);
        detachObserver.observe(document.documentElement || document, {
            childList: true,
            subtree: true,
        });
    }

    function wakeRetries() {
        activeOwners.forEach((owner) => {
            const state = states.get(owner);
            if (!state || state.retryTimer === null) return;
            if (!owner.isConnected && state.everConnected) {
                retire(owner);
                return;
            }
            clearTimeout(state.retryTimer);
            state.retryTimer = null;
            void attempt(owner, state);
        });
        syncLifecycleWakes();
    }

    function wakeVisibleRetries() {
        if (document.visibilityState === 'visible') wakeRetries();
    }

    function scheduleRetry(owner, state) {
        if (!isCurrent(owner, state)) return;
        if (!owner.isConnected && state.everConnected) {
            retire(owner);
            return;
        }
        if (state.retryTimer !== null) clearTimeout(state.retryTimer);
        const delay = RETRY_DELAYS_MS[Math.min(state.retryIndex, RETRY_DELAYS_MS.length - 1)];
        state.retryIndex = Math.min(state.retryIndex + 1, RETRY_DELAYS_MS.length - 1);
        state.retryTimer = setTimeout(() => {
            state.retryTimer = null;
            void attempt(owner, state);
        }, delay);
        state.retryTimer.unref?.();
        track(owner);
        syncLifecycleWakes();
    }

    function cancelCatalogRequest() {
        const request = catalogRequest;
        catalogRequest = null;
        request?.cancel();
    }

    function wakeActiveOwners() {
        activeOwners.forEach((owner) => {
            const state = states.get(owner);
            if (!state || state.retired) return;
            if (state.retryTimer !== null) clearTimeout(state.retryTimer);
            state.retryTimer = null;
            state.retryIndex = 0;
            if (state.pending) {
                state.wakeRequested = true;
            } else {
                void attempt(owner, state);
            }
        });
        syncLifecycleWakes();
    }

    function listCatalog() {
        if (catalog?.length) return Promise.resolve(catalog);
        if (catalogRequest) return catalogRequest.promise;
        const list = MoonfallCloud?.cardbacks?.list;
        if (typeof list !== 'function') {
            return Promise.reject(new Error('Cardback catalog unavailable'));
        }
        const epoch = catalogEpoch;
        let settled = false;
        let timeoutId = null;
        const request = {
            epoch,
            cancel: null,
            promise: null,
        };
        request.promise = new Promise((resolve, reject) => {
            const settle = (error, entries) => {
                if (settled) return;
                settled = true;
                if (timeoutId !== null) clearTimeout(timeoutId);
                timeoutId = null;
                const isAuthoritative = catalogRequest === request && catalogEpoch === epoch;
                if (!error && isAuthoritative && Array.isArray(entries) && entries.length > 0) {
                    catalog = entries;
                    resolve(entries);
                } else {
                    reject(error || new Error(
                        isAuthoritative
                            ? 'Cardback catalog returned no entries'
                            : 'Cardback catalog request superseded',
                    ));
                }
                if (catalogRequest === request) catalogRequest = null;
            };
            request.cancel = () => settle(new Error('Cardback catalog request superseded'));
            timeoutId = setTimeout(() => {
                settle(new Error('Cardback catalog request timed out'));
            }, CATALOG_TIMEOUT_MS);
            timeoutId.unref?.();
            Promise.resolve()
                .then(() => list.call(MoonfallCloud.cardbacks))
                .then(
                    (entries) => settle(null, entries),
                    (error) => settle(error),
                );
        });
        catalogRequest = request;
        return request.promise;
    }

    function entryUrl(entry) {
        // cardbacks:list is the cardback authority and already resolves its
        // storage ID. The generic media API indexes card/character identities,
        // so querying it with a cardback imageId would be a false fallback.
        return sanitizeUrl(entry?.imageUrl);
    }

    async function attempt(owner, state) {
        if (!isCurrent(owner, state) || state.pending) return;
        if (owner.isConnected) state.everConnected = true;
        else if (state.everConnected || Date.now() - state.createdAt >= DETACHED_GRACE_MS) {
            retire(owner);
            return;
        }
        state.pending = true;
        try {
            const entries = await listCatalog();
            if (!isCurrent(owner, state)) return;
            const entry = entries.find((candidate) => candidate?.slug === state.slug);
            const url = entryUrl(entry);
            if (!isCurrent(owner, state)) return;
            if (!url) {
                // A positive catalog is reusable only when it contains this
                // semantic identity with truthful art. A missing/placeholder
                // row may be a stale projection and must be fetched again.
                if (catalog === entries) catalog = null;
                scheduleRetry(owner, state);
                return;
            }
            state.callback({ kind: 'custom-ready', slug: state.slug, url });
            retire(owner);
        } catch (_) {
            if (isCurrent(owner, state)) scheduleRetry(owner, state);
        } finally {
            state.pending = false;
            if (state.wakeRequested && isCurrent(owner, state)) {
                state.wakeRequested = false;
                if (state.retryTimer !== null) clearTimeout(state.retryTimer);
                state.retryTimer = null;
                void attempt(owner, state);
            }
        }
    }

    function observe(owner, value, callback) {
        if (!owner || typeof callback !== 'function') return { dispose() {} };
        retire(owner);
        const slug = normalizedSlug(value);
        owner.dataset.cardbackArtSlug = slug;
        if (slug === 'default') {
            callback({ kind: 'default', slug, url: STOCK_URL });
            return { dispose: () => retire(owner) };
        }

        const state = {
            slug,
            callback,
            retryIndex: 0,
            retryTimer: null,
            pending: false,
            wakeRequested: false,
            retired: false,
            everConnected: owner.isConnected,
            createdAt: Date.now(),
        };
        states.set(owner, state);
        track(owner);
        callback({ kind: 'custom-pending', slug, url: '' });
        void attempt(owner, state);
        return { dispose: () => retire(owner) };
    }

    function cssUrl(url) {
        return `url(${JSON.stringify(url)})`;
    }

    function apply(owner, slug, { onChange } = {}) {
        return observe(owner, slug, (descriptor) => {
            CardBackRaster?.clear?.(owner);
            owner.style.setProperty(
                '--cardback-image',
                descriptor.kind === 'custom-pending' ? 'none' : cssUrl(descriptor.url),
            );
            if (typeof onChange === 'function') onChange(descriptor);
            if (descriptor.url) {
                Promise.resolve().then(() => {
                    if (!owner.isConnected || owner.dataset.cardbackArtSlug !== descriptor.slug) return;
                    try {
                        Promise.resolve(CardBackRaster?.paint?.(owner)).catch(() => {});
                    } catch (_) { /* CSS remains authoritative. */ }
                });
            }
        });
    }

    /**
     * The URL of a back by its semantic slug, for a warm that has no owner
     * yet (the match loading screen warms the player's own back while the
     * server builds the match). One catalog attempt, no retry of its own:
     * the owner that later displays the back keeps `observe`'s retries.
     * Resolves '' when there is no truthful art; never rejects.
     */
    function resolve(value) {
        const slug = normalizedSlug(value);
        if (slug === 'default') return Promise.resolve(STOCK_URL);
        return listCatalog().then(
            (entries) => entryUrl(entries.find((candidate) => candidate?.slug === slug)),
            () => '',
        );
    }

    function prime(entries) {
        if (!Array.isArray(entries) || entries.length === 0) return;
        catalogEpoch += 1;
        cancelCatalogRequest();
        catalog = entries;
        wakeActiveOwners();
    }

    function invalidateCatalog() {
        catalogEpoch += 1;
        cancelCatalogRequest();
        catalog = null;
        wakeActiveOwners();
    }

    function clear(owner) {
        if (!owner) return;
        retire(owner);
        CardBackRaster?.clear?.(owner);
        delete owner.dataset.cardbackArtSlug;
        owner.style.removeProperty('--cardback-image');
    }

    function disposeAllForTests() {
        catalogEpoch += 1;
        cancelCatalogRequest();
        [...activeOwners].forEach(retire);
        catalog = null;
    }

    CardbackArt = {
        STOCK_URL,
        apply,
        clear,
        observe,
        prime,
        resolve,
        sanitizeUrl,
        invalidateCatalog,
        disposeAllForTests,
    };
})();

export { CardbackArt };
